<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SGPDashboard from './SGPDashboard.vue';
import SGPCustomer from './SGPCustomer.vue';
import SGPDialogs from './SGPDialogs.vue';
import SGPPreRegistration from './SGPPreRegistration.vue';

const props = defineProps({
  currentChat: { type: Object, default: () => ({}) },
});

// i18n
const { t: $t } = useI18n();

// Auto-identification signal surfaced by the backend silent lookup.
// Reads directly from the chat's sender so we react via actioncable
// (contact_updated) without any extra store wiring.
const senderAttributes = computed(
  () => props.currentChat?.meta?.sender?.custom_attributes || {}
);
const autoIdStatus = computed(
  () => senderAttributes.value.sgp_identification_status || null
);
const autoIdClienteId = computed(
  () => senderAttributes.value.sgp_cliente_id || null
);
const autoIdContratoId = computed(
  () => senderAttributes.value.sgp_contrato_id || null
);
const isAutoIdentified = computed(() => autoIdStatus.value === 'matched');

// Main state
const innerActiveIndex = ref(0); // 0: Dashboard, 1: Atendimento
const identified = ref(null);
const selectedContractId = ref(null);
const preTabVisible = ref(false);
const preContext = ref(null);

// Computed properties
const hasCustomer = computed(() => !!identified.value);
const customerName = computed(() => {
  if (!identified.value) return '—';
  return (
    identified.value.nome ||
    identified.value.name ||
    identified.value.razao_social ||
    identified.value.clienteNome ||
    '—'
  );
});

// Refs for child components
const dialogsRef = ref(null);

function handleTabChange(index) {
  if (index === 1) {
    // If no customer identified, open the identify dialog
    if (!hasCustomer.value) {
      dialogsRef.value?.open('identify');
      // Don't change tab when there's no customer
      return;
    }
  }
  // Only change tab if we have a customer or if it's not the customer tab
  innerActiveIndex.value = index;
}

function closeCustomer() {
  // Clear customer data
  identified.value = null;
  selectedContractId.value = null;
  // Return to dashboard tab after closing customer
  innerActiveIndex.value = 0;
}

function onCustomerIdentified(customer) {
  identified.value = customer;

  // Set the first contract as selected if available
  if (customer.contratos && customer.contratos.length > 0) {
    const firstContract = customer.contratos[0];
    const contractId =
      firstContract.id ??
      firstContract.contrato ??
      firstContract.contrato_id ??
      firstContract.codigo ??
      firstContract.numero ??
      firstContract.clienteContrato ??
      firstContract.contratoId;
    selectedContractId.value =
      contractId !== undefined && contractId !== null
        ? String(contractId)
        : null;
  }

  // Switch to customer tab
  innerActiveIndex.value = 1;
}

function onOpenDialog(action) {
  if (action === 'pre') {
    dialogsRef.value?.open('pre');
    return;
  }
  dialogsRef.value?.open(action);
}

function onOpenPromiseDialog() {
  dialogsRef.value?.open('promise');
}

function updateSelectedContractId(value) {
  selectedContractId.value = value;
}

// Handle connectivity and service orders updates from dialogs
function onConnectivityUpdated() {
  // This can be used to trigger refresh in customer component
  // For now, the customer component handles its own refreshing
}

function onServiceOrdersUpdated() {
  // This can be used to trigger refresh in customer component
  // For now, the customer component handles its own refreshing
}

function onPreViabilitySuccess(payload) {
  preTabVisible.value = true;
  preContext.value = payload;
  innerActiveIndex.value = 2;
}

function closePreRegistration() {
  preTabVisible.value = false;
  preContext.value = null;
  innerActiveIndex.value = 0;
}
</script>

<template>
  <div class="h-full flex flex-col">
    <div
      v-if="isAutoIdentified && !hasCustomer"
      class="flex items-center gap-2 px-3 py-2 text-xs border-b border-n-weak bg-n-alpha-1 text-n-slate-12"
    >
      <fluent-icon icon="checkmark-circle" size="14" class="text-n-teal-9" />
      <span class="flex-1">
        {{
          $t('SGP.AUTO_IDENTIFIED', {
            contrato: autoIdContratoId || autoIdClienteId,
          })
        }}
      </span>
    </div>
    <div class="flex flex-col min-h-0 flex-1">
      <div class="flex border-b border-n-weak">
        <woot-tabs
          :index="innerActiveIndex"
          :border="false"
          class="w-full"
          @change="handleTabChange"
        >
          <woot-tabs-item
            :index="0"
            :name="$t('SGP.TABS.DASHBOARD')"
            :show-badge="false"
            is-compact
            class="[&_a]:pt-1"
          />
          <woot-tabs-item
            :index="1"
            :name="hasCustomer ? customerName : $t('SGP.TABS.CUSTOMER')"
            :show-badge="false"
            is-compact
            class="[&_a]:pt-1"
          />
          <woot-tabs-item
            v-if="preTabVisible"
            :index="2"
            :name="$t('SGP.TABS.PRE_REGISTRATION')"
            :show-badge="false"
            is-compact
            class="[&_a]:pt-1"
          />
        </woot-tabs>
      </div>

      <div class="flex-1 overflow-y-auto">
        <!-- Dashboard -->
        <SGPDashboard
          v-show="innerActiveIndex === 0"
          :has-customer="hasCustomer"
          @open-dialog="onOpenDialog"
        />

        <!-- Customer -->
        <SGPCustomer
          v-if="innerActiveIndex === 1"
          :identified="identified"
          :selected-contract-id="selectedContractId"
          :current-chat="currentChat"
          @update:selected-contract-id="updateSelectedContractId"
          @close-customer="closeCustomer"
          @open-promise-dialog="onOpenPromiseDialog"
        />

        <SGPPreRegistration
          v-if="preTabVisible && innerActiveIndex === 2"
          :context="preContext"
          @close="closePreRegistration"
        />
      </div>
    </div>

    <!-- Dialogs -->
    <SGPDialogs
      ref="dialogsRef"
      :selected-contract-id="selectedContractId"
      :identified="identified"
      @customer-identified="onCustomerIdentified"
      @close-customer="closeCustomer"
      @connectivity-updated="onConnectivityUpdated"
      @service-orders-updated="onServiceOrdersUpdated"
      @pre-viability-success="onPreViabilitySuccess"
    />
  </div>
</template>
